import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppContextNonNull } from "@contexts/AppContext";
import EndpointsEnum from "@models/EndpointsEnum";
import ILoadUserDTO from "@models/DTO/ILoadUserDTO";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import { FilterBarVersion } from "@models/ITokenSettings";
import UserService from "@services/UserService";
import TokenService from "@services/TokenService";
import { getFilterBarMode } from "@utils/filterBarVersion";

type Props = {
    className?: string;
    /** Never render: the screen always uses the legacy search section (ecommerce Shipments). */
    forceLegacy?: boolean;
};

/**
 * "Try the new filters" / "Back to classic filters" link for the screens that ship the FilterBar
 * (Orders, Quotes, Shipments, Purchase Orders). One setting drives every screen.
 *
 * Writes only `filter_bar_version` through the token settings update (REST `PATCH /v1/tokens/:guid/settings`,
 * the same TokenService.updateSettings path as the socket event), then reloads the user model so the screen
 * re-renders with the chosen search UI. REST is used deliberately: the socket event's acknowledgement makes the
 * Orders screen re-run its current query, which races the first search of the UI being switched to.
 * Hidden for tokens stamped `filter_bar_v2_only`, and for screens that pass `forceLegacy` (ecommerce Shipments).
 */
export const FilterBarVersionToggle = (props: Props) => {
    const { userModel, supplierModel, vendorModel, socket, uiContext } = useAppContextNonNull();
    const navigate = useNavigate();
    const location = useLocation();
    const [pending, setPending] = useState(false);

    const currentToken = UserService.getCurrentToken(userModel);
    const mode = getFilterBarMode(currentToken?.settings, { forceLegacy: props.forceLegacy });

    useEffect(() => {
        // The user model refresh re-renders this component with the new setting; clear the pending state then.
        setPending(false);
    }, [userModel]);

    if (!mode.showToggle || !currentToken) {
        return null;
    }

    const setVersion = async (filter_bar_version: FilterBarVersion) => {
        setPending(true);

        try {
            await TokenService.updateTokenSettings(currentToken.guid, { filter_bar_version }, userModel, supplierModel, vendorModel);

            // Both search UIs push their filters into the URL and read it back on mount as a deep link.
            // Clear it so the UI being switched to starts from its own defaults (default preset / saved legacy filters).
            if (location.search) {
                navigate({ pathname: location.pathname, search: "" }, { replace: true });
            }

            const payload: ILoadUserDTO = { userGuid: userModel.guid, userToken: userModel.token };
            socket.emit(EndpointsEnum.AppLoadUser, payload);
        } catch (error) {
            console.error("Failed to update filter_bar_version", error);
            setPending(false);
            uiContext.notifyFn(null, { message: "error_updating_settings", level: NotificationLevelEnum.error });
        }
    };

    return (
        <button
            type={"button"}
            className={"btn btn-link btn-sm p-0 d-print-none text-nowrap " + (props.className || "")}
            disabled={pending}
            data-testid={"filter-bar-version-toggle"}
            onClick={() => setVersion(mode.useFilterBar ? "legacy" : "v2")}>
            <i className={(mode.useFilterBar ? "fas fa-undo" : "fas fa-sparkles") + " me-1"} />
            <FormattedMessage id={mode.useFilterBar ? "back_to_classic_filters" : "try_new_filters"} />
        </button>
    );
};

export default FilterBarVersionToggle;
